import {
  IMonteCarloResults,
  IMonteCarloYear
} from "../../../../components/calculator/Calculator";
import React, {useEffect, useState} from "react";
import {
  Box, Collapse, IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, Typography,
} from "@mui/material";
import {numberToMoneyFormat} from "../../../../stores/StoreUtilities";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const MonteCarloTable = ({
  year,
  monteCarloResults,
  onChange
} : {
  year: number
  monteCarloResults: IMonteCarloResults
  onChange?: (year: number) => any
}) => {
  const [currentYear, setCurrentYear] = useState<number>(0)
  const [result, setResult] = useState<IMonteCarloResults | undefined>()
  const [readMore, setReadMore] = useState<boolean>(false)

  const styles = {
    th: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.1,
      // width: 50,
      height: 30,
      minHeight: 30,
      // width: 100,
      // maxWidth: 100,
      whiteSpace: "nowrap",
      // maxWidth: 30,
      // textAlign: "right",
      // verticalAlign: "bottom",
      // transformOrigin: "bottom center",
      // transform: "rotate(40deg)",
      // border: "1px solid red"
    },
    tb: {
      fontSize: 14,
      // minWidth: 100,
      // width: 100,
      // maxWidth: 100,
    },
    trRetired: {
      backgroundColor: "rgba(183, 206, 175, 0.8)"
    },
    trHisLifeExpectancy: {
      backgroundColor: "rgba(153, 206, 224, 0.8)"
    },
    trHerLifeExpectancy: {
      backgroundColor: "rgba(255, 176, 190, 0.8)"
    },
    legendLabel: {
      fontSize: 14,
      marginLeft:8,
      marginRight: 16,
      lineHeight: 1.0
    },
    description: {
      fontSize: 15
    },
    readMoreButton: {
      display: "inline-block",
      padding: 0,
      margin: 0,
      paddingBottom: "3px",
      maxWidth: 20,
      fontSize: 15
    }
  }

  useEffect(() => {
    if (year) {
      setCurrentYear(year)
    }
    setResult(monteCarloResults)
  }, [year, monteCarloResults])

  // const handleChange = (year: number) => {
  //   if (onChange) {
  //     setCurrentYear(year)
  //     onChange(year)
  //   }
  // }

  // const getYoYChange = (fc: FutureCalc, net: number) => {
  //   let change = 0
  //   if (fc!.futureYears.length > 0) {
  //     change = net / fc!.futureYears[0].currentLiquidAssets
  //   }
  //   return change
  // }

  const renderYear = (year: IMonteCarloYear, index: number) => {
    return (
      <TableRow key={index}>
        <TableCell sx={styles.tb} key={`tr-${index}-1`}>{year.year}</TableCell>
        <TableCell sx={styles.tb} key={`tr-${index}-2`}><span style={{whiteSpace:"nowrap"}}>{`${year.age1}${year.age2 ? ' / ' + year.age2 : ''}`}</span></TableCell>
        {year.balances.map((balance: number, index2: number) => [
          <TableCell sx={styles.tb} align="right" key={`balance-${index2}`}>
            {numberToMoneyFormat(balance, 0)}
          </TableCell>
        ])}
      </TableRow>
    )
  }

  // const renderStats = () => {
  //   const means = Array<number>(futureCalcs.length)
  //   const stdDevs = Array<number>(futureCalcs.length)
  //
  //   futureCalcs.forEach((fc: FutureCalc, index: number) => {
  //     const rates = fc.futureYears.map((fy: IFutureYear) => fy.growthRate)
  //     const mean = rates.reduce((a, b) => a + b, 0) / rates.length
  //     means[index] = mean
  //     const variance = rates.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / rates.length
  //     stdDevs[index] = Math.sqrt(variance)
  //   })
  //
  //   return ([
  //     <TableRow>
  //       <TableCell sx={styles.th} colSpan={2} align="right">Mean Rate</TableCell>
  //       {means.map((mean: number) => [
  //         <TableCell sx={styles.tb} align="right"></TableCell>,
  //         <TableCell sx={styles.tb} align="right">{numberToPercentFormat(mean)}</TableCell>,
  //       ])}
  //     </TableRow>,
  //       <TableRow>
  //         <TableCell sx={styles.th} colSpan={2} align="right">Standard Deviation</TableCell>
  //         {stdDevs.map((stdDev: number) => [
  //           <TableCell sx={styles.tb} align="right"></TableCell>,
  //           <TableCell sx={styles.tb} align="right">{numberToPercentFormat(stdDev)}</TableCell>
  //         ])}
  //       </TableRow>
  //   ])
  // }

  // const splitLabel = (label: string) => {
  //   const parts = label.split(" / ")
  //   if (parts.length > 1) {
  //     return <span>{parts[0]}<br/>{parts[1]}</span>
  //   } else {
  //     return label
  //   }
  // }

  const renderDescription = () => {
    const planName = result!.labels[result!.labels.length - 1].split("(")[0]

    return (
      <Box display="flex" mb={1}>
        <Box display="flex" flexGrow={1} width="100%" pt="2px">
          <Collapse collapsedSize={18} in={readMore}>
            <Typography variant="body2" sx={styles.description} gutterBottom>
              The simulation randomly samples from historical annual returns (1978–2024) based on your selected asset allocation. It runs multiple iterations using your current budget inputs and portfolio, generating a range of potential outcomes. Results are displayed across percentiles—10th, 25th, 50th, 75th, and 90th—providing insight into how your portfolio might perform under varying market conditions. The 10th percentile represents a more pessimistic outcome, where only 10% of simulations performed worse, making it a useful reference for potential worst-case scenarios. In contrast, the 90th percentile reflects a more optimistic outcome, with only 10% of simulations performing better, highlighting a potential best-case scenario. The 50th percentile represents the median outcome, where half of the simulations performed better and half performed worse.
            </Typography>
            <Typography variant="body2" sx={styles.description} gutterBottom>
              This simulation is hypothetical and intended for illustrative purposes only. It does not predict actual investment results or guarantee future performance. Past performance is not indicative of future outcomes. Because market returns are inherently uncertain, actual portfolio results may differ significantly from the scenarios shown. The Retirement Confidence Score represents a probability based on historical assumptions and should not be interpreted as a guarantee. This tool does not constitute personalized investment advice, and users should consult a qualified financial professional before making any financial or investment decisions based on this analysis.
            </Typography>
            <Typography variant="body2" sx={styles.description} gutterBottom>
              The <b>{planName}</b> models your plan using the selected growth assumptions—whether fixed rates, historical returns, or a bad timing sequence. It provides a consistent, non-random projection to help you evaluate how your plan performs under those specific conditions.
            </Typography>
          </Collapse>
        </Box>
        <Box display="flex" flexGrow={0} width={24} flexDirection="column" justifyContent="flex-start">
          <IconButton onClick={() => setReadMore(!readMore)} sx={styles.readMoreButton}>
            {readMore ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
          </IconButton>
        </Box>
      </Box>
    )
  }

  if (!result) {
    return null
  }

  return (
    <Box display="flex" flexGrow={1} flexDirection="column" mt={2}>
      <Typography variant="h3" color="primary">Monte Carlo Details</Typography>
      {renderDescription()}
      <TableContainer component={Paper}>
        <Table size="small" sx={{width: "max-content"}}>
          <TableHead>
            <TableRow >
              <TableCell sx={styles.th} key="th-1">Year</TableCell>
              <TableCell sx={styles.th} key="th-2">Age(s)</TableCell>
              {result.labels.map((label: string, index: number) => {
                if (index === 5) {
                  label = label.substring(0, label.indexOf("("))
                }
                return <TableCell sx={styles.th} align="right" key={`th-${index}`}>{label} Balance</TableCell>
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {result.years.map((year: IMonteCarloYear, index: number) =>
              renderYear(year, index)
            )}
            {/*{renderStats()}*/}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default MonteCarloTable